import { useState, useRef, useEffect } from "react";
import { Dialog, Transition } from "@headlessui/react";
import React, { ComponentPropsWithoutRef, Fragment, createContext, useContext } from "react";
import { twClassNames } from "../../helpers/twClassNames";
import Button, { ButtonType } from "../Button";
import useAutoFocus from "../../hooks/useAutoFocus";
import classNames from "classnames";

const ModalContext = createContext<{ onClose: () => void }>({ onClose: () => {} });
let modalStack: string[] = [];

type RfModalProps = React.HTMLAttributes<HTMLDivElement> & {
    isOpen: boolean;
    onClose: () => void;
    escapeToClose?: boolean;
    disableSubmitOnEnter?: boolean;
    role?: "dialog" | "alertdialog";
};
export const RfModal = ({
    children,
    isOpen,
    onClose,
    escapeToClose = true,
    className,
    disableSubmitOnEnter = false,
    ...props
}: RfModalProps) => {
    const autoFocusRef = useAutoFocus([isOpen]);
    const wrapperRef = useRef<HTMLDivElement | null>(null);

    const [modalId] = useState<string>(Math.random().toString(36));

    function submitOnEnter(e: KeyboardEvent) {
        if (e.key === "Enter") {
            if (modalId !== modalStack[0]) return;

            if (e.target instanceof HTMLTextAreaElement) {
                if (!e.metaKey && !e.ctrlKey) {
                    return;
                }
            } else if (e.metaKey || e.ctrlKey) {
                return;
            }

            const dialogElem = wrapperRef.current as unknown as HTMLElement | null;
            if (!dialogElem) return;

            const footerElem = dialogElem.querySelector(".rfModalFooter");

            let toClick: HTMLElement | null = null;

            if (footerElem) {
                toClick = footerElem.querySelector("button.rfModalSubmitButton");
            } else {
                const buttons = dialogElem.querySelectorAll(
                    "button.primary, button.rfModalSubmitButton"
                );
                toClick = buttons.length > 0 ? (buttons[buttons.length - 1] as HTMLElement) : null;
            }

            setTimeout(function () {
                if (toClick) toClick.click();
            }, 0);

            if (toClick) {
                e.preventDefault();
                e.stopPropagation();
            }
        }
    }

    useEffect(() => {
        if (isOpen && !disableSubmitOnEnter) {
            modalStack.unshift(modalId);
            window.addEventListener("keydown", submitOnEnter);

            return () => {
                modalStack = modalStack.filter((id) => id !== modalId);
                window.removeEventListener("keydown", submitOnEnter);
            };
        }
    }, [isOpen, disableSubmitOnEnter, modalId]);

    return (
        <ModalContext.Provider value={{ onClose }}>
            <Transition.Root appear show={isOpen} as={Fragment} unmount>
                <Dialog
                    ref={autoFocusRef}
                    as="div"
                    className={twClassNames("relative z-50", className)}
                    onClose={escapeToClose ? onClose : () => {}}
                    open={isOpen}
                    {...props}
                >
                    <div ref={wrapperRef as React.MutableRefObject<HTMLDivElement | null>}>
                        {children}
                    </div>
                </Dialog>
            </Transition.Root>
        </ModalContext.Provider>
    );
};

RfModal.Overlay = ({ children, className, ...props }: ComponentPropsWithoutRef<"div">) => (
    <Transition.Child
        as={Fragment}
        enter="ease-out duration-300"
        enterFrom="opacity-0"
        enterTo="opacity-100"
        leave="ease-in duration-200"
        leaveFrom="opacity-100"
        leaveTo="opacity-0"
    >
        <div className={twClassNames("fixed inset-0 bg-black bg-opacity-25", className)} {...props}>
            {children}
        </div>
    </Transition.Child>
);

RfModal.Title = ({ children, className, ...props }: ComponentPropsWithoutRef<"h1">) => (
    <Dialog.Title
        as="h1"
        className={twClassNames(
            "m-0 flex-grow pr-6 text-lg font-semibold text-gray-900",
            className
        )}
        {...props}
    >
        {children}
    </Dialog.Title>
);

RfModal.Panel = ({
    children,
    className,
    positionClassName,
    fullscreen = false,
    animations = true,
    allowClickPropagation = false,
    allowMouseUpPropagation = false,
    ...props
}: ComponentPropsWithoutRef<"div"> & {
    positionClassName?: string;
    fullscreen?: boolean;
    animations?: boolean;
    allowClickPropagation?: boolean;
    allowMouseUpPropagation?: boolean;
}) => {
    const animationClasses = animations
        ? {
              enter: "ease-out duration-300",
              enterFrom: "opacity-0",
              enterTo: "opacity-100",
              leave: "ease-in duration-200",
              leaveFrom: "opacity-100",
              leaveTo: "opacity-0"
          }
        : {};
    return (
        <div className="fixed inset-0 overflow-y-auto">
            <div
                className={twClassNames(
                    "modalContainer flex min-h-full items-center justify-center text-center",
                    fullscreen ? "" : "p-4",
                    positionClassName
                )}
                onClick={(e) => (allowClickPropagation ? undefined : e.stopPropagation())}
                onMouseDown={(e) => (allowClickPropagation ? undefined : e.stopPropagation())}
                onMouseUp={(e) =>
                    allowMouseUpPropagation || allowClickPropagation
                        ? undefined
                        : e.stopPropagation()
                }
            >
                <Transition.Child as={Fragment} {...animationClasses}>
                    <Dialog.Panel
                        className={twClassNames(
                            "dialogPanel flex w-full flex-col items-stretch justify-start overflow-hidden bg-white text-left align-middle shadow-xl",
                            fullscreen ? "h-screen" : "max-w-4xl rounded-lg p-6",
                            className,
                            { "transform transition-all": animations }
                        )}
                        {...props}
                    >
                        {children}
                    </Dialog.Panel>
                </Transition.Child>
            </div>
        </div>
    );
};

RfModal.Header = ({ children, className, ...props }: ComponentPropsWithoutRef<"div">) => (
    <div
        className={twClassNames("flex items-center justify-between border-b px-6 py-5", className)}
        {...props}
    >
        {children}
    </div>
);

RfModal.CloseButton = ({
    children,
    className,
    onClick,
    onClickOverride,
    left = false,
    top = "top-5",
    ...props
}: ComponentPropsWithoutRef<"button"> & {
    left?: boolean;
    top?: string;
    onClickOverride?: () => void;
}) => {
    const { onClose } = useContext(ModalContext);
    return (
        <button
            className={twClassNames(
                left ? "left-5" : "right-5",
                top,
                "closeButton absolute z-50 h-6 w-6 rounded-full pt-[2px] text-sm text-gray-500 transition duration-200 hover:bg-red-100 hover:text-red-500 focus:outline-none",
                className
            )}
            onMouseDown={(e) => e.stopPropagation()}
            onMouseUp={(e) => e.stopPropagation()}
            onClick={(e) => {
                e.stopPropagation();
                if (onClickOverride) {
                    onClickOverride();
                } else {
                    onClick?.(e);
                    onClose();
                }
            }}
            {...props}
        >
            <i className="fal fa-times inline"></i>
            {children}
        </button>
    );
};

RfModal.BackButton = ({
    children,
    className,
    onClick,
    left = false,
    top = "top-6",
    ...props
}: ComponentPropsWithoutRef<"button"> & { left?: boolean; top?: string }) => {
    const { onClose } = useContext(ModalContext);
    return (
        <button
            className={twClassNames(
                left ? "left-6" : "right-6",
                top,
                "closeButton absolute z-50 h-6 w-6 rounded-full pt-[2px] text-sm text-gray-500 transition duration-200 hover:bg-gray-100 hover:text-gray-700 focus:outline-none",
                className
            )}
            onClick={(e) => {
                onClick?.(e);
                onClose();
            }}
            {...props}
        >
            <i className="fal fa-arrow-left inline"></i>
            {children}
        </button>
    );
};

RfModal.Footer = ({
    submitButtonText = "Submit",
    cancelButtonText,
    onSubmit = () => {},
    onCancel = () => {},
    isFullWidth = false,
    hasTopBorder = false,
    className = "",
    submitButtonType = "primary",
    submitButtonProps = {},
    cancelButtonProps = {},
    footerCustomLeftSide = null,
    closeOnCancel = true,
    closeOnSubmit = false
}: {
    submitButtonText?: string | React.ReactNode;
    cancelButtonText?: string;
    onSubmit?: () => void;
    onCancel?: () => void;
    isFullWidth?: boolean;
    hasTopBorder?: boolean;
    className?: string;
    submitButtonType?: ButtonType;
    submitButtonProps?: Omit<ComponentPropsWithoutRef<"button">, "type">;
    cancelButtonProps?: Omit<ComponentPropsWithoutRef<"button">, "type">;
    footerCustomLeftSide?: React.ReactNode;
    closeOnCancel?: boolean;
    closeOnSubmit?: boolean;
}) => {
    const { onClose } = useContext(ModalContext);

    return (
        <div
            className={twClassNames(
                "rfModalFooter",
                "flex flex-wrap items-center justify-between gap-2",
                className,
                hasTopBorder ? "border-t-1 px-6 py-4" : ""
            )}
        >
            {cancelButtonText ? (
                <Button
                    className={isFullWidth ? "flex-1" : undefined}
                    type={"secondary" as ButtonType}
                    size="medium"
                    onClick={() => {
                        onCancel();
                        if (closeOnCancel) {
                            onClose();
                        }
                    }}
                    {...cancelButtonProps}
                >
                    {cancelButtonText}
                </Button>
            ) : (
                !isFullWidth && (
                    <div className="flex-1 text-sm text-gray-700">{footerCustomLeftSide}</div>
                )
            )}
            <Button
                className={classNames(isFullWidth ? "flex-1" : undefined, "rfModalSubmitButton")}
                type={submitButtonType}
                size="medium"
                onClick={() => {
                    onSubmit();
                    if (closeOnSubmit) {
                        onClose();
                    }
                }}
                {...submitButtonProps}
            >
                {submitButtonText}
            </Button>
        </div>
    );
};

RfModal.CustomFooter = ({
    children,
    hasTopBorder = false,
    className = ""
}: {
    children?: React.ReactNode;
    hasTopBorder?: boolean;
    className?: string;
}) => {
    return (
        <div
            className={twClassNames(
                "flex items-center justify-between gap-2",
                className,
                hasTopBorder && "border-t-1 border-gray-200 px-6 py-4"
            )}
        >
            {children}
        </div>
    );
};
